import * as React from 'react';
import { ReceiptLineTooltip } from 'components/business/receipts/ReceiptLineTooltip';
import { SelectedOption } from '@tock/shared/utils/option';
import { TockFeeCallout } from './TockFeeCallout';
import { Typography } from 'omakase';
import { consumer, core, safeEnumCompare } from '@tock/coretypes';
import { deliveryServiceProviderDisplayName } from '@tock/shared/utils/offerings';
import { getAmountDue, isPastPurchase } from '@tock/shared/utils/purchase';
import { isEmpty, isNil, map, partition, sortBy, sum } from 'es-toolkit/compat';
import { isTaxCenter, isTaxLibrary } from '@tock/shared/utils/taxUtils';
import OptionsItemizedReceiptSection from './OptionsItemizedReceiptSection';
import ReceiptLine from 'components/business/receipts/ReceiptLine';
import protos from 'utils/protos';

const { Discount, TicketType } = protos;

interface ItemizedReceiptProps {
  business: consumer.IBusinessConfig | core.IBusinessPublic;
  compAmount: number;
  customCharge: Array<core.PurchasedCustomCharge | core.CustomChargeOrder>;
  deliveryServiceProvider: core.DeliveryServiceProvider | null | undefined;
  extraChargeDetails?: core.ExtraChargeDetails;
  isGratuityForThirdPartyDelivery?: boolean;
  isVisitEdit: boolean;
  options: Dictionary<SelectedOption>;
  partySize: number;
  purchase: core.PurchaseOrder | core.PastPurchase;
  purchaseFees: core.IFeePrice[];
  showTockFeeIfRequired?: boolean;
  title?: string;
  /**
   * There are cases where we are showing the receipt before a refund action is taken
   * in this case you can send a number to this to show a refund amount
   * currently used in the cancellation flow
   */
  potentialRefund?: number;
  alwaysShowAmountDue?: boolean;
  /**
   * this is used in the case of a past purchase that is canceled and refunded
   * will show the total from purchase.refund if this is true as a line item after total
   */
  alwaysShowRefund?: boolean;
  alwaysInlineTaxesAndFees?: boolean;
  ticketType?: core.ITicketType;
}

const getTaxTotal = (purchase: core.PurchaseOrder | core.PastPurchase): number => {
  const usingTaxLibrary = isTaxLibrary(purchase.taxStrategy);
  const usingTaxCenter = isTaxCenter(purchase.taxStrategy);

  if (usingTaxLibrary || usingTaxCenter) {
    return sum(purchase.purchasedTaxLineItem.map((tax) => tax.amountCents ?? 0));
  } else {
    return purchase.tax ?? 0;
  }
};

const getTaxLineItems = (purchase: core.PurchaseOrder | core.PastPurchase): JSX.Element[] => {
  const usingTaxLibrary = isTaxLibrary(purchase.taxStrategy);
  const usingTaxCenter = isTaxCenter(purchase.taxStrategy);

  if (usingTaxCenter) {
    return [<ReceiptLine key="tax" amount={purchase.tax} lineItem={'Taxes'} data-testid="tax" />];
  } else if (usingTaxLibrary) {
    const sortedTaxLineItems = sortBy(purchase.purchasedTaxLineItem, ['name']);
    return map(sortedTaxLineItems, (taxLineItem, id) => {
      return (
        <ReceiptLine
          key={`tax-${id}`}
          amount={taxLineItem.amountCents ?? 0}
          lineItem={`${taxLineItem.name} `}
          percentage={(taxLineItem.rate ?? 0) / 100.0}
          data-testid={`tax`}
        />
      );
    });
  } else if (purchase.tax > 0) {
    return [
      <ReceiptLine
        key="tax"
        amount={purchase.tax}
        lineItem={'Tax '}
        percentage={purchase.taxRate / 100.0}
        data-testid="tax"
      />,
    ];
  } else {
    return [];
  }
};

const getFeeLineItem = (fee: core.IFeePrice, idx: number, note?: string) => {
  return (
    <div key={`fee-${idx}-${fee.price?.id}`}>
      <ReceiptLine
        key={idx}
        amount={fee.price?.amount ?? 0}
        lineItem={fee.price?.name}
        data-testid="fee"
      />
      <Typography variant="caption" color="fgSubtle">
        {note}
      </Typography>
    </div>
  );
};

const getTaxesAndPostSubtotalFeesContent = (
  taxLineItems: React.ReactNode[],
  postSubtotalFeeLineItems: React.ReactNode[],
  inlineTaxesAndFees: boolean
) => {
  if (inlineTaxesAndFees) {
    return <>{[...taxLineItems, ...postSubtotalFeeLineItems]}</>;
  }
  return (
    <>
      {taxLineItems.length > 0 && (
        <>
          <div className="Receipt-content--title">
            <Typography variant="body02Strong" component="h2">
              Taxes
            </Typography>
          </div>
          {taxLineItems}{' '}
        </>
      )}
      {taxLineItems.length > 0 && postSubtotalFeeLineItems.length > 0 && (
        <div className="Receipt-separator" />
      )}
      {postSubtotalFeeLineItems.length > 0 && (
        <>
          <div className="Receipt-content--title">
            <Typography variant="body02Strong" component="h2">
              Fees
            </Typography>
          </div>
          {postSubtotalFeeLineItems}
        </>
      )}
    </>
  );
};

const ItemizedReceipt: React.FC<ItemizedReceiptProps> = ({
  alwaysInlineTaxesAndFees = false,
  alwaysShowAmountDue = false,
  alwaysShowRefund = false,
  business,
  compAmount,
  customCharge,
  deliveryServiceProvider,
  extraChargeDetails,
  isGratuityForThirdPartyDelivery,
  isVisitEdit,
  options,
  partySize,
  potentialRefund,
  purchase,
  purchaseFees,
  showTockFeeIfRequired,
  title,
  ticketType,
}) => {
  const serviceChargeName =
    !isNil(extraChargeDetails) && !isEmpty(extraChargeDetails.serviceChargeName)
      ? extraChargeDetails.serviceChargeName
      : 'Service charge';
  const showDepositHelpText =
    (!isVisitEdit &&
      ticketType?.variety &&
      safeEnumCompare(ticketType.variety, TicketType.Variety, TicketType.Variety.DEPOSIT)) ||
    undefined;

  const eventFeeRate = purchase.eventFeeRate;
  const hasEventFee = !isNil(eventFeeRate) && eventFeeRate > 0;
  const taxLineItems = getTaxLineItems(purchase);
  const subtotalDiscounts = (purchase.discount as core.Discount[]).filter((discount) =>
    safeEnumCompare(
      discount.discountType,
      Discount.DiscountType,
      Discount.DiscountType.SUBTOTAL_COMP
    )
  );

  const postTotalDiscounts = (purchase.discount as core.Discount[]).filter(
    (discount) =>
      !safeEnumCompare(
        discount.discountType,
        Discount.DiscountType,
        Discount.DiscountType.SUBTOTAL_COMP
      )
  );
  const tockFee: core.ITockFee | undefined = purchase.tockFee ?? undefined;

  const [postSubtotalFees, preSubtotalFees] = partition(purchaseFees, (fee) => fee.isPostSubtotal);

  const inlineTaxesAndFees =
    alwaysInlineTaxesAndFees || (taxLineItems.length === 1 && postSubtotalFees.length === 0);
  const taxesAndFeesAmount =
    getTaxTotal(purchase) + sum(postSubtotalFees.map((fee) => fee.price?.amount ?? 0));
  const postSubtotalFeeLineItems = postSubtotalFees.map((fee, idx) =>
    getFeeLineItem(fee, idx, inlineTaxesAndFees ? undefined : 'This fee helps us operate Tock')
  );
  const taxesAndPostSubtotalFeesContent = getTaxesAndPostSubtotalFeesContent(
    taxLineItems,
    postSubtotalFeeLineItems,
    inlineTaxesAndFees
  );

  const amountDue = getAmountDue(purchase);
  const getTaxesAndFeesAriaLabel = (
    taxLineItemCount: number,
    postSubtotalFeesCount: number
  ): string => {
    const taxesAriaLabel = taxLineItemCount > 0 ? 'Taxes' : '';
    const andAriaLabel = taxLineItemCount > 0 && postSubtotalFeesCount > 0 ? ' & ' : '';
    const feesAriaLabel = postSubtotalFeesCount > 0 ? 'Fees' : '';
    return taxesAriaLabel + andAriaLabel + feesAriaLabel + ' (opens popover)';
  };

  return (
    <>
      <div className="Receipt-container--section Receipt-container--lastSection">
        {title && (
          <div className="Receipt-content--title">
            <Typography variant="heading04" component="h2">
              {title}
            </Typography>
          </div>
        )}
        <div className="ConsumerReceipt" data-testid="consumer-receipt">
          <OptionsItemizedReceiptSection
            partySize={partySize}
            ticketTypePrice={purchase.ticketTypePrice}
            ticketPriceInformation={purchase.ticketPriceInformation}
            ticketTypeName={purchase.ticketTypeName}
            purchaseId={purchase.id}
            options={options}
            showDepositHelpText={showDepositHelpText}
          />
          {preSubtotalFees.length > 0 && (
            <div>{preSubtotalFees.map((fee, idx) => getFeeLineItem(fee, idx))}</div>
          )}
          {customCharge && customCharge.length > 0 && (
            <div>
              {customCharge.map(
                (
                  customCharge: core.PurchasedCustomCharge | core.CustomChargeOrder,
                  idx: number
                ) => {
                  return (
                    <ReceiptLine
                      key={'charge-' + idx}
                      amount={customCharge.priceCents}
                      lineItem={customCharge.customChargeName}
                    />
                  );
                }
              )}
            </div>
          )}

          <div>
            {purchase.subtotal > 0 && (
              <>
                <div className="Receipt-separator" />
                <ReceiptLine
                  data-testid="subtotal-amount"
                  amount={purchase.subtotal}
                  lineItem={'Subtotal'}
                />
              </>
            )}
            {compAmount > 0 && (
              <ReceiptLine amount={-compAmount} lineItem={'Complimentary discount'} />
            )}
            {subtotalDiscounts.length > 0 &&
              subtotalDiscounts.map((discount: core.Discount, idx: number) => {
                return (
                  <ReceiptLine
                    key={'subtotalDiscount-' + idx}
                    amount={-discount.amountCents}
                    lineItem={discount.description + ' '}
                  />
                );
              })}
            {purchase.deliveryServiceFeeCents > 0 && (
              <ReceiptLine
                amount={purchase.deliveryServiceFeeCents}
                lineItem={`Delivery fee (fulfilled by ${deliveryServiceProviderDisplayName(
                  deliveryServiceProvider
                )})`}
              />
            )}
            {hasEventFee && (
              <ReceiptLine
                amount={purchase.eventFeeCents}
                lineItem={'Event fee '}
                percentage={eventFeeRate / 100.0}
              />
            )}
            {purchase.customFeeCents > 0 && (
              <ReceiptLine
                data-testid="custom-fee"
                amount={purchase.customFeeCents}
                lineItem={purchase.customFeeName + ' '}
                percentage={purchase.customFeeRate / 100.0}
              />
            )}
            {purchase.serviceCharge > 0 && (
              <ReceiptLine
                data-testid="service-charge"
                amount={purchase.serviceCharge}
                lineItem={serviceChargeName + ' '}
                percentage={purchase.serviceChargeRate / 100.0}
                toolTip={
                  serviceChargeName === 'Service charge' && (
                    <ReceiptLineTooltip
                      ariaLabel="Service charge explanation"
                      popoverId="service-charge-explanation-popover"
                    >
                      This is a charge asked for and collected by the business, not a Tock fee
                    </ReceiptLineTooltip>
                  )
                }
                maximumFractionDigits={2}
              />
            )}
            {purchase.gratuityCents > 0 && (
              <ReceiptLine
                amount={purchase.gratuityCents}
                lineItem={'Gratuity '}
                percentage={purchase.selectedGratuityRate / 100.0}
                toolTip={isGratuityForThirdPartyDelivery ? <> (for delivery)</> : undefined}
                data-testid="receipt-gratuity"
                maximumFractionDigits={2}
              />
            )}
            {inlineTaxesAndFees && taxesAndPostSubtotalFeesContent}
            {!inlineTaxesAndFees && taxesAndFeesAmount > 0 ? (
              <ReceiptLine
                amount={taxesAndFeesAmount}
                lineItem={
                  <>
                    {taxLineItems.length > 0 && 'Taxes'}
                    {taxLineItems.length > 0 && postSubtotalFees.length > 0 && ' & '}
                    {postSubtotalFees.length > 0 && 'Fees'}
                  </>
                }
                toolTip={
                  <ReceiptLineTooltip
                    data-testid="taxes-and-fees"
                    ariaLabel={getTaxesAndFeesAriaLabel(
                      taxLineItems.length,
                      postSubtotalFees.length
                    )}
                    popoverId="taxes-and-fees-popover"
                  >
                    {taxesAndPostSubtotalFeesContent}
                  </ReceiptLineTooltip>
                }
                data-testid="receipt-taxes-and-fees"
              />
            ) : null}
            {postTotalDiscounts.map((discount: core.Discount, idx: number) => (
              <ReceiptLine
                key={'discount-' + idx}
                amount={-discount.amountCents}
                lineItem={discount.description + ' '}
              />
            ))}
            {purchase.inStoreChargeCents > 0 && (
              <ReceiptLine
                key="in-store-charge-cents"
                amount={purchase.inStoreChargeCents}
                lineItem={'Orders made in-store'}
              />
            )}
            <div className="Receipt-separator" />
          </div>
          <ReceiptLine
            amount={purchase.totalPrice}
            bold
            lineItem={'Total'}
            data-testid="receipt-total"
          />

          {purchase.tockGiftCardBalanceUsedCents > 0 ? (
            <ReceiptLine
              amount={-purchase.tockGiftCardBalanceUsedCents}
              lineItem="Tock gift card"
            />
          ) : null}

          {showTockFeeIfRequired && tockFee && purchase.totalPrice && (
            <TockFeeCallout businessName={business.name ?? undefined} tockFee={tockFee} />
          )}
          {amountDue > 0 || purchase.tockGiftCardBalanceUsedCents > 0 || alwaysShowAmountDue ? (
            <>
              {(isPastPurchase(purchase) && purchase.paymentCardPaidCents > 0) ||
              purchase.netAmountPaidCents > 0 ? (
                <>
                  <div className="Receipt-separator" />
                  <ReceiptLine
                    amount={
                      isPastPurchase(purchase)
                        ? -purchase.paymentCardPaidCents
                        : -purchase.netAmountPaidCents
                    }
                    bold
                    lineItem="Paid"
                  />
                </>
              ) : null}

              {(amountDue > 0 ||
                alwaysShowAmountDue ||
                purchase.tockGiftCardBalanceUsedCents > 0) &&
                !(purchase.netAmountPaidCents > purchase.totalPrice) && (
                  <>
                    <div className="Receipt-separator" />
                    <ReceiptLine
                      amount={amountDue}
                      bold
                      lineItem="Amount due"
                      data-testid="amount-due"
                    />
                  </>
                )}
            </>
          ) : null}
          {isPastPurchase(purchase) &&
          purchase.paymentCardPaidCents > 0 &&
          purchase.tockGiftCardBalanceUsedCents === 0 &&
          amountDue === 0 &&
          !alwaysShowAmountDue ? (
            <>
              <div className="Receipt-separator" />
              <ReceiptLine
                data-testid="receipt-paid"
                amount={(purchase as core.PastPurchase).paymentCardPaidCents}
                bold
                lineItem="Paid"
              />
            </>
          ) : null}
          {!isPastPurchase(purchase) &&
          purchase.netAmountPaidCents > purchase.totalPrice &&
          !purchase.transferPurchaseToken ? (
            <>
              <div className="Receipt-separator" />
              <ReceiptLine
                amount={purchase.netAmountPaidCents - purchase.totalPrice}
                bold
                refund
                lineItem={'Refund due'}
                data-testid="receipt-refund-due"
              />
            </>
          ) : null}
          {potentialRefund ? (
            <>
              <div className="Receipt-separator" />
              <ReceiptLine
                amount={potentialRefund}
                bold
                lineItem={'Refund due'}
                data-testid="receipt-refund-due"
              />
            </>
          ) : null}
          {alwaysShowRefund && isPastPurchase(purchase) && purchase?.refund.length > 0 ? (
            <>
              <div className="Receipt-separator" />
              <ReceiptLine
                amount={purchase.refund.reduce((tally, refund) => tally + (refund?.amount ?? 0), 0)}
                bold
                refund
                lineItem={'Refund'}
                data-testid="receipt-refund-due"
              />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ItemizedReceipt;
